import type { ReactNode } from "react";

import { Link } from "@alpacahq/ui/old";

import type {
  DOCUMENT_TYPES,
  DocumentRequestResponse,
} from "~/v2/api/rest/account";
import { normalizeDocType } from "~/v2/utils/normalizeDocType";

type SelectOption = {
  value: string;
  label: string;
};

interface AccountDocumentsText {
  title: string;
  description: ReactNode;
  selectOptions?: SelectOption[];
}

const AccountDocumentTextMap = new Map<
  (typeof DOCUMENT_TYPES)[number],
  | AccountDocumentsText
  | ((request: DocumentRequestResponse) => AccountDocumentsText)
>([
  [
    "date_of_birth_verification",
    {
      title: "Date of Birth Verification",
      description: "Upload a document that shows your name and date of birth.",
      selectOptions: [
        { value: "birth_certificate", label: "Birth Certificate" },
        { value: "drivers_license", label: "Drivers License" },
        { value: "valid_gov_id", label: "Valid Government Issued ID" },
      ],
    },
  ],
  [
    "social_security_number_verification",
    {
      title: "Social Security Number Verification",
      description:
        "Upload a document that shows your name and social security number.",
      selectOptions: [
        { value: "ssn_card", label: "Social Security Card" },
        {
          value: "certified_irs_letter",
          label: "Certified IRS Letter",
        },
        { value: "w2", label: "W2" },
        {
          value: "other_official_tax_doc",
          label: "Other Official Tax Document",
        },
      ],
    },
  ],
  [
    "address_verification",
    {
      title: "Address Verification",
      description: "Upload a document that shows your name and address.",
      selectOptions: [
        { value: "utility_bill", label: "Utility Bill" },
        { value: "bank_statement", label: "Bank Statement" },
        { value: "lease_agreement", label: "Lease Agreement" },
        { value: "valid_gov_id", label: "Valid Government Issued ID" },
      ],
    },
  ],
  [
    "identity_verification",
    {
      title: "Identity Verification",
      description: (
        <span>
          Upload a valid government issued ID.{" "}
          <span className="font-bold">Note:</span> The uploaded photo should be
          a photo of the physical ID. Scanned, digital, black and white copies,
          and photos of a screen are not accepted.
        </span>
      ),
      selectOptions: [
        { value: "drivers_license", label: "Driver's License" },
        { value: "valid_gov_id", label: "Valid Government Issued ID" },
        { value: "passport", label: "Passport" },
      ],
    },
  ],
  [
    "tax_id_verification",
    {
      title: "Tax ID Verification",
      description: "Upload documentation to verify your Tax ID.",
      selectOptions: [
        { value: "tax_certificate", label: "Tax Certificate" },
        {
          value: "valid_gov_id",
          label: "Valid Government Issued ID",
        },
        {
          value: "other_official_tax_doc",
          label: "Other Official Tax Document",
        },
      ],
    },
  ],
  [
    "account_approval_letter",
    {
      title: "Account Approval Letter",
      description: (
        <span>
          Upload a written letter from your compliance officer granting
          permission for you to carry an account with Alpaca.{" "}
          <span className="font-bold">Note:</span> Alpaca does not send
          duplicate account statements and/or trade confirmations at this time.
          This approval document must explicitly agree to these conditions.
        </span>
      ),
    },
  ],
  [
    "selfie",
    {
      title: "Selfie with Government Issued ID",
      description:
        "A selfie of you holding your government issued photo ID. Please ensure all details on the front of your ID are fully visible.",
    },
  ],
  [
    "entity_registration",
    {
      title: "Entity Registration",
      description:
        "Please upload documentation of proof that the entity is actively registered and in good standing with a government authority.",
    },
  ],
  [
    "w_8bene",
    {
      title: "W-8BEN-E",
      description: (
        <span>
          Please provide an updated W-8BEN-E ensuring all information is filled
          out in each part with the most current information. These are used to
          certify foreign status and claim tax treaty benefits for non-U.S.
          entities. Required for tax withholding purposes. You can fill out and
          download the template here:{" "}
          <Link href="https://www.irs.gov/pub/irs-pdf/iw8bene.pdf">
            W-8BEN-E Template
          </Link>
        </span>
      ),
    },
  ],
  [
    "company_formation",
    {
      title: "Company Formation",
      description:
        "Please upload documentation of proof that the entity is actively registered and in good standing with a government authority.",
      selectOptions: [
        { value: "operating_agreement", label: "Operating Agreement" },
        {
          value: "certificate_of_formation",
          label: "Certificate of Formation",
        },
        {
          value: "articles_of_incorporation",
          label: "Articles of Incorporation",
        },
        { value: "bylaws", label: "Bylaws" },
      ],
    },
  ],
  [
    "other",
    ({ message }: DocumentRequestResponse) => ({
      title: "Other",
      description: message,
    }),
  ],
]);

// Add overrides for entity documents here
export const EntityAccountDocumentTextOverrideMap: Map<
  (typeof DOCUMENT_TYPES)[number],
  AccountDocumentsText
> = new Map([
  [
    "address_verification",
    {
      title: "Address Verification",
      selectOptions: [
        { value: "utility_bill", label: "Utility Bill" },
        { value: "bank_statement", label: "Bank Statement" },
        { value: "lease_agreement", label: "Lease Agreement" },
        { value: "other", label: "Other" },
      ],
      description:
        "We need a document that confirms the entity's physical or mailing address. Acceptable documents are: Utility bill, Bank statement, Lease agreement, or any other document that includes the entity's name and address.",
    },
  ],
  [
    "tax_id_verification",
    {
      title: "Tax ID Verification",
      description:
        "Please provide documentation to verify the entity's Tax ID. Examples of acceptable documents include: Tax Certificate or any official government-issued document showing the entity's Tax ID number.",
      selectOptions: [
        { value: "tax_certificate", label: "Tax Certificate" },
        { value: "other", label: "Other" },
      ],
    },
  ],
]);

export const getRequestedDocumentContent = (
  isLegalEntity: boolean,
  request: DocumentRequestResponse,
): AccountDocumentsText | undefined => {
  const docType = normalizeDocType(request.doc_type);
  let val = AccountDocumentTextMap.get(docType);

  if (isLegalEntity && EntityAccountDocumentTextOverrideMap.has(docType)) {
    val = EntityAccountDocumentTextOverrideMap.get(docType);
  }

  if (typeof val === "function") {
    return val(request);
  }
  return val;
};
